import { useState, FormEvent } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('prenom', formData.prenom);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telephone', formData.telephone);
      formDataToSend.append('sujet', formData.sujet);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d63lgbr0723ekv2itej0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block">
              <span className="text-sm font-semibold tracking-wider text-orange-500 uppercase">Contactez-nous</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight font-serif">
              Parlons de votre projet
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Que vous soyez un particulier, une institution ou une entreprise, nous sommes à votre écoute pour répondre à vos questions et construire ensemble des solutions adaptées à vos besoins.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              
              {/* Formulaire */}
              <div>
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">Envoyez-nous un message</h2>
                  <p className="text-slate-600">Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.</p>
                </div>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-semibold text-slate-700 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="Votre nom"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="prenom" className="block text-sm font-semibold text-slate-700 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        id="prenom"
                        name="prenom"
                        required
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="Votre prénom"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="votre.email@exemple.fr"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telephone" className="block text-sm font-semibold text-slate-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="sujet" className="block text-sm font-semibold text-slate-700 mb-2">
                      Sujet *
                    </label>
                    <select
                      id="sujet"
                      name="sujet"
                      required
                      value={formData.sujet}
                      onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="inscription-formation">Inscription à une formation</option>
                      <option value="accompagnement-individuel">Demande d'accompagnement individuel</option>
                      <option value="partenariat">Proposition de partenariat</option>
                      <option value="information">Demande d'information</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none"
                      placeholder="Décrivez votre demande en quelques lignes (500 caractères maximum)..."
                    ></textarea>
                    <p className="text-sm text-slate-500 mt-2">{formData.message.length}/500 caractères</p>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                      <p className="text-green-700 font-semibold">Votre message a été envoyé avec succès ! Nous vous répondrons rapidement.</p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center gap-3">
                      <i className="ri-error-warning-fill text-2xl text-red-500"></i>
                      <p className="text-red-700 font-semibold">Une erreur est survenue. Veuillez réessayer.</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>
                </form>
              </div>
              
              {/* Informations de contact */}
              <div className="space-y-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">Nos coordonnées</h2>
                  <p className="text-slate-600">Vous pouvez également nous contacter directement par téléphone ou email.</p>
                </div>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
                        <i className="ri-phone-line text-2xl text-orange-500"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">Téléphone</h4>
                      <a href="tel:+33650997467" className="text-slate-600 hover:text-orange-500 transition-colors cursor-pointer">
                        +33 6 50 99 74 67
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                        <i className="ri-mail-line text-2xl text-slate-900"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">Email</h4>
                      <a href="mailto:contact@pulsat-pfc.fr" className="text-slate-600 hover:text-orange-500 transition-colors cursor-pointer">
                        contact@pulsat-pfc.fr
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
                        <i className="ri-map-pin-line text-2xl text-orange-500"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">Adresse</h4>
                      <p className="text-slate-600">
                        71 Bis Avenue Jean Jaurès<br />
                        93600 Aulnay-sous-Bois, France
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                        <i className="ri-time-line text-2xl text-slate-900"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-1">Horaires d'ouverture</h4>
                      <p className="text-slate-600">
                        Lundi - Vendredi : 9h00 - 18h00<br />
                        Samedi : 9h00 - 12h00<br />
                        Dimanche : Fermé
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-8">
                  <h4 className="text-lg font-bold text-slate-900 mb-4">Suivez-nous</h4>
                  <div className="flex gap-4">
                    <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white hover:bg-orange-500 transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white hover:bg-orange-500 transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white hover:bg-orange-500 transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center text-white hover:bg-orange-500 transition-colors cursor-pointer">
                      <i className="ri-instagram-fill text-xl"></i>
                    </a>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-3xl p-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-information-line text-2xl text-orange-500"></i>
                    <h4 className="text-lg font-bold text-slate-900">Bon à savoir</h4>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    Nous nous engageons à vous répondre dans un délai de 48 heures ouvrées. Pour les demandes urgentes, n'hésitez pas à nous appeler directement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 font-serif mb-4">Où nous trouver</h2>
              <p className="text-xl text-slate-600">Venez nous rencontrer dans nos locaux</p>
            </div>
            
            <div className="rounded-3xl overflow-hidden shadow-2xl h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2621.8!2d2.4950!3d48.9350!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6124c!2s71+Bis+Avenue+Jean+Jaur%C3%A8s%2C+93600+Aulnay-sous-Bois!5e0!3m2!1sfr!2sfr!4v1234567890123!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation PULSAR FC"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
